import { toExcelAmount, toExcelDate, toExcelInteger, toExcelQuantity } from 'components/xlsx/excelCells';
import minBy from 'lodash/minBy';
import { getDaysOverdue } from 'pages/invoices/util/invoiceUtil';
import { ClassifierData } from 'types/classifiers';
import { InvoiceExportListItemDto } from 'types/invoices';
import { getClassifierText, t } from 'utils/getTranslations';

export const mapInvoicesToCSVFormat = (
    invoices: InvoiceExportListItemDto[],
    classifiers: Record<string, ClassifierData>
) => {
    return invoices?.map((invoice) => {
        const currTypeInvoice = t('invoices.export.currency-type.invoice');
        const currTypePayment = t('invoices.export.currency-type.payment');
        const currTypeBase = t('invoices.export.currency-type.base');

        const oldestContractSignedDate = minBy(
            invoice.serviceAgreements.filter((item) => item.contractSignedAt != null),
            'contractSignedAt'
        )?.contractSignedAt;

        const oldestContractSentDate = minBy(
            invoice.serviceAgreements.filter((item) => item.contractSentAt != null),
            'contractSentAt'
        )?.contractSentAt;

        return {
            [t('invoices.export.issued-by-office')]: invoice.issuedByOffice,
            [t('invoices.export.issued-by-company')]: invoice.issuedByCompany,
            [t('invoices.export.date-of-invoice')]: toExcelDate(invoice.invoiceDate),
            [t('invoices.export.service-start-date')]: toExcelDate(invoice.serviceStartDate),
            [t('invoices.export.service-end-date')]: toExcelDate(invoice.serviceEndDate),
            [t('invoices.export.due-date')]: toExcelDate(invoice.dueDate),
            [t('invoices.export.days-overdue')]: toExcelInteger(getDaysOverdue(invoice.dueDate)),
            [t('invoices.export.invoice-type')]: getClassifierText(
                classifiers['INVOICE_TYPE'],
                invoice.invoiceTypeCode
            ),
            [t('invoices.export.invoice-number')]: invoice.invoiceNumber,
            [t('invoices.export.credited-invoice-number')]: invoice.debitInvoice?.invoiceNumber,
            [t('invoices.export.accounting-revenue-type')]: getClassifierText(
                classifiers['ACCOUNTING_REVENUE_TYPE'],
                invoice.accountingRevenueTypeCode
            ),
            [t('invoices.export.payer-region')]: invoice.payer.region,
            [t('invoices.export.payer-name')]: invoice.payer.name,
            [t('invoices.export.payer-reg-nr-or-id-code')]: invoice.payer.regNrOrIdCode,
            [t('invoices.export.payer-vat-number')]: invoice.payer.vatNumber,
            [t('invoices.export.payer-country')]: getClassifierText(classifiers['COUNTRY'], invoice.payer.countryCode),
            [t('invoices.export.client-name')]: invoice.clientParties?.map(({ name }) => name).join(', '),
            [t('invoices.export.primary-contact-name')]: invoice.primaryContacts?.map(({ name }) => name).join(', '),
            [t('invoices.export.issued-for-cases')]: invoice.issuedForCases?.map(({ name }) => name).join(', '),
            [t('invoices.export.used-hours')]: toExcelQuantity(invoice.usedHours),
            [t('invoices.export.billed-hours')]: toExcelQuantity(invoice.billedHours),
            [t('invoices.export.invoiced-hours')]: toExcelQuantity(invoice.invoicedHours),
            [t('invoices.export.fee-arrangement')]: (invoice.feeArrangementTypeCodes ?? [])
                .map((code) => getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], code))
                .sort()
                .join(', '),
            [t('invoices.export.paid-on-date')]: toExcelDate(invoice.paidOnDate),

            [t('invoices.export.currency', { currencyType: currTypeInvoice })]: invoice.invoiceCurrencyTypeCode,
            [t('invoices.export.fees-before-adjustments', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.feesBeforeAdjustments
            ),
            [t('invoices.export.discount-amount', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.discountAdjustments
            ),
            [t('invoices.export.other-adjustments-amount', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.otherAdjustments
            ),
            [t('invoices.export.fees-after-adjustments-before-vat', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.feesAfterAdjustmentsBeforeVat
            ),
            [t('invoices.export.expenses', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.expenses
            ),
            [t('invoices.export.non-vat-expenses', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.nonVatExpenses
            ),
            [t('invoices.export.adjusted-fees-expenses-non-vat-expenses', { currencyType: currTypeInvoice })]:
                toExcelAmount(invoice.totalsInvoiceCurrency?.totalWithoutVat),
            [t('invoices.export.total-vat', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.totalVat
            ),
            [t('invoices.export.total-amount', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.totalAmount
            ),
            [t('invoices.export.prepayment', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.prepayment
            ),
            [t('invoices.export.payable-amount-prepayment-deducted', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.payableAmount
            ),
            [t('invoices.export.remains-unpaid', { currencyType: currTypeInvoice })]: toExcelAmount(
                invoice.totalsInvoiceCurrency?.remainsUnpaid
            ),
            [t('invoices.export.debt-collection-notes')]: invoice.debtNotes,
            [t('invoices.export.specific-remarks')]: invoice.billingInstructions,
            [t('invoices.export.invoice-status')]: getClassifierText(
                classifiers['INVOICE_STATUS_TYPE'],
                invoice.invoiceStatusTypeCode
            ),
            [t('invoices.export.tc-from-date')]: toExcelDate(invoice.tcFromDate),
            [t('invoices.export.tc-until-date')]: toExcelDate(invoice.tcUntilDate),
            [t('invoices.export.service-agreement-number')]: invoice.serviceAgreements
                ?.map(({ contractNumber }) => contractNumber)
                .filter((name) => name !== null && name.length > 0)
                .join(', '),
            [t('invoices.export.service-agreement-sent-date')]: toExcelDate(oldestContractSentDate),
            [t('invoices.export.service-agreement-signed-on-date')]: toExcelDate(oldestContractSignedDate),
            [t('invoices.export.case-responsible-lawyer')]: invoice.responsibleLawyers
                ?.map(({ name }) => name)
                .join(', '),
            [t('invoices.export.case-supervising-partner')]: invoice.supervisingPartners
                ?.map(({ name }) => name)
                .join(', '),
            [t('invoices.export.case-related-practice-area')]: (invoice.practiceAreas?.slice() ?? []).sort().join(', '),

            [t('invoices.export.currency', { currencyType: currTypePayment })]: invoice.paymentCurrencyTypeCode,
            [t('invoices.export.fees-before-adjustments', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.feesBeforeAdjustments
            ),
            [t('invoices.export.discount-amount', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.discountAdjustments
            ),
            [t('invoices.export.other-adjustments-amount', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.otherAdjustments
            ),
            [t('invoices.export.expenses', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.expenses
            ),
            [t('invoices.export.non-vat-expenses', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.nonVatExpenses
            ),
            [t('invoices.export.adjusted-fees-expenses-non-vat-expenses', { currencyType: currTypePayment })]:
                toExcelAmount(invoice.totalsPaymentCurrency?.totalWithoutVat),
            [t('invoices.export.total-vat', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.totalVat
            ),
            [t('invoices.export.total-amount', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.totalAmount
            ),
            [t('invoices.export.prepayment', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.prepayment
            ),
            [t('invoices.export.payable-amount-prepayment-deducted', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.payableAmount
            ),
            [t('invoices.export.remains-unpaid', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.totalsPaymentCurrency?.remainsUnpaid
            ),

            [t('invoices.export.currency', { currencyType: currTypeBase })]: invoice.baseCurrencyTypeCode,
            [t('invoices.export.fees-before-adjustments', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.feesBeforeAdjustments
            ),
            [t('invoices.export.discount-amount', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.discountAdjustments
            ),
            [t('invoices.export.other-adjustments-amount', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.otherAdjustments
            ),
            [t('invoices.export.expenses', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.expenses
            ),
            [t('invoices.export.non-vat-expenses', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.nonVatExpenses
            ),
            [t('invoices.export.adjusted-fees-expenses-non-vat-expenses', { currencyType: currTypeBase })]:
                toExcelAmount(invoice.totalsBaseCurrency?.totalWithoutVat),
            [t('invoices.export.total-vat', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.totalVat
            ),
            [t('invoices.export.total-amount', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.totalAmount
            ),
            [t('invoices.export.prepayment', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.prepayment
            ),
            [t('invoices.export.payable-amount-prepayment-deducted', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.payableAmount
            ),
            [t('invoices.export.remains-unpaid', { currencyType: currTypeBase })]: toExcelAmount(
                invoice.totalsBaseCurrency?.remainsUnpaid
            ),
            [t('invoices.export.customer-latest-invoice-total-vat', { currencyType: currTypePayment })]: toExcelAmount(
                invoice.latestInvoiceTotalsPaymentCurrency?.totalVat
            ),
            [t('invoices.export.customer-latest-invoice-total-amount', { currencyType: currTypePayment })]:
                toExcelAmount(invoice.latestInvoiceTotalsPaymentCurrency?.totalAmount),
        };
    });
};
